import clsx from "clsx";
import React, { useState } from "react";
import Container from "./Container";
import DemoCard, { DemoChecklistCard, SummaryCard, AccuracyCard, TimeToCodeCard } from "./DemoCard";
import heroAssets from "../data/heroAssets";

type SectionProps = {
  className?: string;
};

const ValuePropositions = ({ className }: SectionProps) => {
  const ORANGE_HEX = "#FF8000";
  const [hovered, setHovered] = useState<number | null>(null);
  const [activeSummaryIds, setActiveSummaryIds] = useState<string[]>([]);

  const blocks = [
    {
      key: "accuracy",
      title: "Leapfrog towards 99% coding accuracy",
      description:
        "Capture missed dx & identify potential overcoding exposure from RADV clawbacks. Boost your RA program’s last mile accuracy.",
      // asset: <DemoChecklistCard />,
      scale: 1.0
    },
    {
      key: "prospective",
      title: "Uncover hidden prospective signals",
      description:
        "Suspect complex conditions buried in longitudinal patient charts, frequently missed by human coders & NLP tools.",
      // asset: (
      //   <DemoCard
      //     title="Suspected Dx"
      //     code="E11.9 | HCC 38"
      //     badgeLabel=""
      //     showActions={false}
      //   />
      // ),
      scale: 0.7
    },
    {
      key: "defensibility",
      title: "Ironclad RADV audit defensibility",
      description:
        "RADV audit defensible evidence packets & rationale behind every code found.",
      // asset: null,
      scale: 0.7
    },
    {
      key: "time-to-code",
      title: "Reduce time-to-code",
      description:
        "Heavy lifting done by VitalStrata RAF Precision AI. Transition human coders into accept/reject & audit roles.",
      // asset: <SummaryCard />,
      scale: 1.0
    },
  ];

  return (
    <section id="value-propositions" className={clsx("py-24", className)}>
      <Container>
        <div className="text-sm sm:text-sm font-base text-gray-400">VITALSTRATA VALUE</div>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl md:text-5xl font-light tracking-tight text-white drop-shadow-lg">
          Uplift & protect RAF integrity
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {blocks.map((b, idx) => (
            <div
              key={b.key}
              className="group rounded-lg p-6 cursor-pointer"
              onMouseEnter={() => setHovered(idx)}
              onMouseLeave={() => setHovered(null)}
            >
              <div className="relative h-44 flex items-center justify-center mb-12">
                <div className={clsx(
                  "transform",
                  b.scale === 0.7 ? "scale-[0.7]" : b.scale === 1.0 ? "scale-100" : "scale-100"
                )}>
                  {b.key === "prospective" && (
                    (() => {
                      const demoB1 =
                        heroAssets.demoSignals.find(
                          (d) => d.memberId === "B" && d.kind === "suspect"
                        ) || heroAssets.demoSignals[0];
                      return (
                        <DemoCard
                          title={(demoB1 as any)?.title}
                          code={(demoB1 as any)?.code}
                          badgeLabel={(demoB1 as any)?.badgeLabel}
                          showActions={(demoB1 as any)?.showActions}
                          bottomTagText={(demoB1 as any)?.bottomTagText}
                          secondarySectionTitle={(demoB1 as any)?.secondarySectionTitle}
                          secondarySectionText={(demoB1 as any)?.secondarySectionText}
                          meatValidatedLetters={(demoB1 as any)?.meatValidatedLetters}
                          penaltyText={(demoB1 as any)?.penaltyText}
                          rationaleList={(demoB1 as any)?.rationale}
                          badgeIcon={(demoB1 as any)?.badgeIcon}
                          accent={hovered === idx}
                          accentColor={ORANGE_HEX}
                        />
                      );
                    })()
                  )}
                  {b.key === "accuracy" && (
                    <AccuracyCard accent={hovered === idx} accentColor={ORANGE_HEX} />
                  )}
                  {b.key === "defensibility" && (() => {
                    const demoAdd =
                      heroAssets.demoSignals.find(
                        (d) => (d as any).id === "demo-A" || d.kind === "add"
                      ) || heroAssets.demoSignals[0];
                    const summary =
                      heroAssets.summaries.find((s) => s.memberId === "A") ||
                      heroAssets.summaries[0];
                    return (
                      <div className="flex items-start gap-4 items-center">
                        <DemoCard
                          title={(demoAdd as any)?.title}
                          code={(demoAdd as any)?.code}
                          badgeLabel={(demoAdd as any)?.badgeLabel}
                          badgeIcon={(demoAdd as any)?.badgeIcon}
                          showActions={(demoAdd as any)?.showActions}
                          primaryActionLabel={(demoAdd as any)?.primaryActionLabel}
                          secondaryActionLabel={(demoAdd as any)?.secondaryActionLabel}
                          penaltyText={(demoAdd as any)?.penaltyText}
                          bottomTagText={(demoAdd as any)?.bottomTagText}
                          meatValidatedLetters={(demoAdd as any)?.meatValidatedLetters}
                          secondarySectionTitle={(demoAdd as any)?.secondarySectionTitle}
                          secondarySectionText={(demoAdd as any)?.secondarySectionText}
                          rationaleList={(demoAdd as any)?.rationale}
                          onRationaleHover={(id) => {
                            if (!id) {
                              setActiveSummaryIds([]);
                              return;
                            }
                            const refs =
                              (demoAdd as any)?.rationale?.find((r: any) => r.id === id)
                                ?.summaryRefs || [];
                            setActiveSummaryIds(refs);
                          }}
                          accent={hovered === idx}
                          accentColor={ORANGE_HEX}
                        />
                        <SummaryCard
                          title={summary?.title}
                          sentenceList={summary?.sentences}
                          disableHighlights={true}
                          activeSentenceIds={activeSummaryIds}
                          accent={hovered === idx}
                          accentColor={ORANGE_HEX}
                        />
                      </div>
                    );
                  })()}
                  {b.key === "time-to-code" && (
                    <TimeToCodeCard accent={hovered === idx} accentColor={ORANGE_HEX} />
                  )}
                </div>
              </div>
              <div
                className="text-2xl font-light text-white group-hover:text-[#FF8000]"
              >
                {b.title}
              </div>
              <p className="mt-2 text-base font-light text-white/80 group-hover:text-[#FF8000]">
                {b.description}
              </p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default ValuePropositions;
